import classNames from 'classnames';
import { StaticImage } from 'gatsby-plugin-image';

import { Hours, Phone } from '~components/AllianceCallService/AllianceCallService';
import Button from '~components/Button/Button';
import { useAccessibleTextColor } from '~globals/common';
import { fireTrackEvent } from '~globals/tracking';
import { useAppSelector } from '~hooks/useRedux';
import CheckIcon from '~images/inline/checkmark-decorative.inline.svg';

type Props = {
    imageurl?: string;
    heading?: string;
    subheading?: string;
    bullet1?: string;
    bullet2?: string;
    bullet3?: string;
    bullet4?: string;
    ctacopy?: string;
    backgroundsurface?: SupportedBackgroundColor;
};

const CallExpectations = ({
    imageurl,
    heading = 'Get support from a benefit advisor with expertise in your area.',
    subheading = "Here's what you can expect when you call:",
    bullet1 = "Start by answering a few easy questions. What's your Medicare number? Who are your doctors? What medications do you take?",
    bullet2 = 'Compare plans in your area that match your needs.',
    bullet3 = 'Explore opportunities for discounts and savings.',
    bullet4 = 'Get the same plan at the same price as the insurance company.',
    ctacopy = 'Chat with an advisor',
    backgroundsurface = 'bg-surface-100',
}: Props) => {
    const throttle = useAppSelector((state) => state.global.throttle);

    const trackingComponentName = 'Call Expectations';

    const initiateLPChat = (e: React.MouseEvent) => {
        e.preventDefault();
        if (document.querySelector('[role="button"].LPMoverlay')) {
            (document.querySelector('[role="button"].LPMoverlay') as HTMLElement).click();
        }
        fireTrackEvent({ page: { action: `${trackingComponentName}: chat with us` } });
    };

    return (
        <section
            className={classNames(
                'font-poppins',
                'px-4 py-6 md:px-6 md:py-9 lg:py-20',
                backgroundsurface
            )}
        >
            <div className="flex flex-col md:flex-row md:justify-center md:gap-6 max-w-1200 mx-auto">
                <div className="max-h-64 md:max-h-full overflow-hidden">
                    {imageurl ? (
                        <img src={imageurl} alt="" className="w-64 mx-auto md:w-auto" />
                    ) : (
                        <StaticImage
                            src="./eve-circle.webp"
                            alt="A friendly eHealth insurance advisor"
                            className="w-64 mx-auto md:w-auto"
                        />
                    )}
                </div>
                <div
                    className={classNames(
                        'relative',
                        'my-8 md:mt-0 md:mb-4 lg:mb-8',
                        'lg:max-w-[690px]'
                    )}
                >
                    <h2
                        className={classNames(
                            useAccessibleTextColor(backgroundsurface, 'text-link-visited'),
                            'font-poppins font-bold',
                            'text-center md:text-start',
                            'text-32 leading-40 md:text-40 md:leading-56 lg:text-48 lg:leading-64'
                        )}
                        dangerouslySetInnerHTML={{ __html: heading }}
                    />
                    <h3
                        className={classNames(
                            useAccessibleTextColor(backgroundsurface),
                            'font-bold mt-7 font-sans',
                            'text-20 leading-7',
                            'lg:text-24 lg:leading-8 lg:text-start'
                        )}
                    >
                        {subheading}
                    </h3>
                    <ul
                        className={classNames(
                            'mt-4 mb-9 md:mb-8 lg:mb-12 gap-4',
                            useAccessibleTextColor(backgroundsurface)
                        )}
                    >
                        {[bullet1, bullet2, bullet3, bullet4].map((text) => {
                            return (
                                <li
                                    key={text.replaceAll(' ', '')}
                                    className="flex flex-row justify-start items-start gap-2 py-2"
                                >
                                    <div className="w-6 lg:w-[34px]">
                                        <CheckIcon className="text-24 lg:text-34 text-icon-primary" />
                                    </div>
                                    <div className="text-16 leading-6 lg:text-18 lg:leading-7 font-sans">
                                        {text}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                    <div className="">
                        {/* mobile */}
                        <div className="md:hidden">
                            <p className="font-bold text-center">Speak to an advisor today</p>
                            <div
                                className={classNames(throttle && throttle !== 'GREEN' && 'hidden')}
                            >
                                <Phone
                                    isbtn="reskin"
                                    classname={classNames(
                                        'text-20 leading-7 my-2',
                                        useAccessibleTextColor(backgroundsurface)
                                    )}
                                    fromComponentName={trackingComponentName}
                                />
                                <p
                                    className={classNames(
                                        'text-center font-bold leading-24',
                                        useAccessibleTextColor(backgroundsurface)
                                    )}
                                >
                                    TTY 711
                                </p>
                                <div className="text-center">
                                    <Hours
                                        isinline
                                        classname={classNames(
                                            useAccessibleTextColor(backgroundsurface)
                                        )}
                                    />
                                </div>
                            </div>
                            <Button
                                styletype="secondary"
                                className="mt-6 w-full"
                                onClick={initiateLPChat}
                            >
                                {ctacopy}
                            </Button>
                        </div>
                        {/* tablet & desktop */}
                        <div className="hidden md:flex flex-col lg:flex-row lg:items-center gap-6">
                            <div
                                className={classNames(
                                    'flex flex-col',
                                    throttle && throttle !== 'GREEN' && 'hidden'
                                )}
                            >
                                <p className="font-bold text-24 leading-32">
                                    Speak to an advisor today
                                </p>
                                <p className="text-32 leading-40 flex lg:flex-col gap-2 lg:gap-0 mr-6">
                                    <Phone
                                        classname={classNames(
                                            'font-bold',
                                            useAccessibleTextColor(
                                                backgroundsurface,
                                                'text-link-visited'
                                            )
                                        )}
                                        fromComponentName={trackingComponentName}
                                    />
                                    <span
                                        className={classNames(
                                            'font-light',
                                            useAccessibleTextColor(backgroundsurface)
                                        )}
                                    >
                                        TTY 711
                                    </span>
                                </p>
                                <Hours
                                    isinline
                                    classname={classNames(
                                        useAccessibleTextColor(backgroundsurface)
                                    )}
                                />
                            </div>
                            <Button
                                styletype="reskin"
                                className="max-w-80"
                                onClick={initiateLPChat}
                            >
                                {ctacopy}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CallExpectations;
